import { lazy, type ComponentType, type LazyExoticComponent } from "react";

/**
 * `lazy()` with built-in recovery from the "stale chunk after deploy"
 * failure mode of any chunked SPA.
 *
 * Why this exists (the bug it solves)
 * -----------------------------------
 * Vite produces hash-named chunks per route (e.g. `LogsView-Cy7EFdJ2.js`).
 * The `index.html` of each build references the hashes from that build.
 * When we deploy a new build, the old hashed chunk files are deleted
 * from S3 (Vite cleans the `dist/` between builds, Amplify replaces
 * the bucket contents).
 *
 * Customers (or QA, or Pepe himself) who already had the SPA open
 * still hold the OLD index.html in their tab. The eager bundle keeps
 * working. The moment they navigate to a route whose chunk hadn't
 * been requested yet, `import("@/features/logs/LogsView")` triggers
 * a network fetch for `/assets/LogsView-Cy7EFdJ2.js` (the OLD hash
 * from the build their tab loaded with) — and S3 returns 404 because
 * only `/assets/LogsView-<NEW_HASH>.js` exists now. The dynamic-import
 * promise rejects with `TypeError: Failed to fetch dynamically
 * imported module: ...` (Vite/Chrome wording) or
 * `error loading dynamically imported module` (Firefox) or
 * `Importing a module script failed` (Safari).
 *
 * `<Suspense>` doesn't have a built-in mechanism to recover from a
 * rejected import promise; the rejection escalates to the nearest
 * `componentDidCatch` boundary (in our case `RootErrorBoundary`),
 * which renders the generic "Something went wrong" fallback. The
 * customer sees a crash for what was a deploy-cutover hiccup.
 *
 * Pepe surfaced 2026-05-25 (ref `0e6e0671` in error-tracking logs):
 * three back-to-back pushes (`aa5465be`, `c009f5d6`, `bb085598`)
 * invalidated every chunk hash; his still-open tab clicked Logs and
 * got the crash.
 *
 * The fix (industry-standard pattern)
 * -----------------------------------
 * Wrap the import factory in a try/catch. If the failure looks like a
 * stale-chunk error, force a single `window.location.reload()` —
 * which fetches the new `index.html` (which references the NEW chunk
 * hashes) and re-mounts the SPA cleanly. If we've already reloaded
 * once this session and the same error fires again, re-throw — that
 * means the failure isn't a deploy-cutover and we should not infinite-
 * loop the user.
 *
 * The reload uses a `sessionStorage` flag (not `localStorage`) so the
 * guard expires when the tab/window closes — every fresh session
 * gets one auto-reload chance again.
 *
 * Why per-import (and not a global window error handler):
 * - The import rejection is the only signal we can reliably attribute
 *   to a stale chunk. Catching every TypeError globally would mask
 *   real bugs as deploy cutovers.
 * - This is the precise integration point — `lazy()` already wants
 *   a factory; wrapping it is one-line ergonomic change in App.tsx.
 * - Sentry/Datadog write similar wrappers internally for the same
 *   reason; it's not Rook-specific cleverness.
 *
 * Limitations
 * - The reload erases the in-page state (open dialogs, unsubmitted
 *   form fields, scroll position). For an internal-tool BFF portal
 *   that's an acceptable trade — the alternative is a hard crash.
 *   When we add a long edit form (Webhooks, Account) and want to
 *   preserve draft state, we can layer on a "new version available"
 *   banner that defers the reload until the user clicks; see
 *   `foundations/PENDING-WORK.md` (deferred) for that follow-up.
 */

/**
 * Cross-browser detection for dynamic-import failure modes.
 *
 * Each browser phrases the same underlying error differently:
 *   - Chromium/Vite default: "Failed to fetch dynamically imported module"
 *   - Firefox:               "error loading dynamically imported module"
 *   - Safari:                "Importing a module script failed"
 *   - Webpack legacy:        "Loading chunk N failed" / "Loading CSS chunk"
 *
 * The pattern matches all four. A false positive here just causes one
 * extra page reload — a false negative crashes the SPA, so we lean
 * permissive.
 *
 * Exported for direct unit testing.
 */
export const isChunkLoadError = (err: unknown): boolean => {
  if (!(err instanceof Error)) return false;
  // The Vite/Chrome message embeds the module URL; the Firefox/Safari
  // variants are shorter. One regex covers all four shapes.
  return /\b(failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|loading chunk|loading css chunk)\b/i.test(
    err.message,
  );
};

/**
 * sessionStorage key used to guard against an infinite reload loop.
 * Set on first chunk failure → reload; cleared on first successful
 * load → next deploy gets a fresh auto-reload chance.
 *
 * Exported for tests that need to assert/manipulate the guard state.
 */
export const CHUNK_RELOAD_FLAG = "rook-portal:chunk-reload-attempted";

/**
 * The actual factory wrapper. Extracted from `lazyWithReload` so
 * tests can `await` it directly without spinning up a React tree.
 *
 * Behaviour:
 *   - Resolve the inner factory; on success, clear the reload flag
 *     (so a future stale-chunk failure can trigger its one reload).
 *   - On failure that looks like a stale chunk AND the flag isn't
 *     set: set the flag, call `window.location.reload()`, and return
 *     a Promise that never resolves. React's `lazy` would otherwise
 *     surface the unresolved Suspense; the reload tears the tree
 *     down before that matters.
 *   - On any other failure (or when the flag IS set): rethrow so the
 *     nearest error boundary handles it normally.
 */
export const loadOrReload = async <T,>(
  factory: () => Promise<T>,
): Promise<T> => {
  try {
    const mod = await factory();
    // Successful load — clear any stale guard from a prior session.
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
    }
    return mod;
  } catch (err) {
    const isStaleChunk = isChunkLoadError(err);
    const alreadyTried =
      typeof sessionStorage !== "undefined" &&
      sessionStorage.getItem(CHUNK_RELOAD_FLAG) !== null;

    if (isStaleChunk && !alreadyTried) {
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem(CHUNK_RELOAD_FLAG, "1");
      }
      if (typeof window !== "undefined" && typeof window.location?.reload === "function") {
        window.location.reload();
      }
      // Park the promise — the reload will tear React down before
      // this resolves. Returning anything that satisfies the type
      // would race the reload and risk a flicker.
      return new Promise<T>(() => {
        /* intentionally never resolves */
      });
    }

    throw err;
  }
};

/**
 * Drop-in replacement for React's `lazy()` that recovers from
 * stale-chunk errors after a deploy. See file header for the full
 * design rationale.
 *
 * Usage in App.tsx — identical signature to `lazy`:
 *
 *   const LogsView = lazyWithReload(() =>
 *     import("@/features/logs/LogsView").then((mod) => ({
 *       default: mod.LogsView,
 *     })),
 *   );
 */
export const lazyWithReload = <T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> => lazy(() => loadOrReload(factory));
